import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { useMountEffect } from "@/hooks/useMountEffect";

import { PrimaryButton, SecondaryButton } from "./Button";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "./Modal";

export type ConfirmCheckboxOption = {
  /** Names this box in `ConfirmResult.checked`. */
  id: string;
  label: React.ReactNode;
  defaultChecked?: boolean;
  /**
   * Blocks confirming until the box is ticked, for a checkbox that is an
   * acknowledgement rather than an extra option. Off by default: a checkbox like
   * "also reset the history table" is a choice, and disabling Confirm for it would
   * strand the user who legitimately wants to leave it unticked.
   */
  required?: boolean;
};

export type ConfirmResult = {
  confirmed: boolean;
  checked: Record<string, boolean>;
};

export type ConfirmProps = {
  title: string;
  message: React.ReactNode;
  type?: "info" | "danger";
  confirmButtonText?: string;
  initialFocus?: "confirm" | "cancel";
  checkboxes?: ConfirmCheckboxOption[];
};

type ConfirmComponentProps<Props = ConfirmProps> = Props & {
  confirm: (result?: ConfirmResult) => void;
  cancel: () => void;
};

const defaultChecked = (checkboxes: ConfirmCheckboxOption[] = []) =>
  Object.fromEntries(
    checkboxes.map((checkbox) => [
      checkbox.id,
      checkbox.defaultChecked ?? false,
    ]),
  );

type PendingConfirm = {
  props: ConfirmProps;
  settle: (result: ConfirmResult | boolean) => void;
};

type ConfirmHost = {
  depth: number;
  open: (pending: PendingConfirm) => void;
};

/**
 * A headlessui Dialog suppresses its own outside-click handling only for dialogs nested
 * inside it in the React tree. A confirm rendered anywhere else lands in
 * `#headlessui-portal-root`, the one place an open Dialog treats as outside itself, so
 * it would close the slide-over behind it and swallow the click. Opening in the deepest
 * mounted host keeps the confirm nested inside whatever is already open.
 */
const hosts: ConfirmHost[] = [];

const deepestHost = () =>
  hosts.reduce<ConfirmHost | null>(
    (deepest, host) =>
      deepest === null || host.depth >= deepest.depth ? host : deepest,
    null,
  );

// Keyed on the property, not its length: a caller building `checkboxes`
// dynamically must still get a `ConfirmResult` when the list comes out empty.
const hasCheckboxes = (props: ConfirmProps) => props.checkboxes !== undefined;

/** The shape `confirm()` and `confirmWithCheckboxes()` each resolve to for "no". */
const refused = (props: ConfirmProps) =>
  hasCheckboxes(props)
    ? { confirmed: false, checked: defaultChecked(props.checkboxes) }
    : false;

const NestingContext = createContext(0);

/**
 * Renders the confirm dialog for the subtree it sits in. `index.tsx` mounts the root
 * one; every `SlideOver` and `Modal` mounts a deeper one via `ConfirmScope`.
 */
export function ConfirmOutlet() {
  const depth = useContext(NestingContext);
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);

  const open = useCallback((next: PendingConfirm) => {
    pendingRef.current = next;
    setPending(next);
  }, []);

  useEffect(() => {
    const host: ConfirmHost = { depth, open };
    hosts.push(host);
    return () => {
      const index = hosts.indexOf(host);
      if (index !== -1) {
        hosts.splice(index, 1);
      }
    };
  }, [depth, open]);

  // The dialog hosting this outlet can close while a confirm is still open, which
  // would otherwise leave the caller awaiting a promise nothing can settle.
  useEffect(
    () => () => {
      const stranded = pendingRef.current;
      if (stranded !== null) {
        pendingRef.current = null;
        stranded.settle(refused(stranded.props));
      }
    },
    [],
  );

  if (pending === null) {
    return null;
  }

  const settle = (result: ConfirmResult | boolean) => {
    if (pendingRef.current === null) {
      return;
    }
    pendingRef.current = null;
    setPending(null);
    pending.settle(result);
  };

  return (
    <ConfirmModal
      {...pending.props}
      confirm={(result?: ConfirmResult) =>
        settle(
          hasCheckboxes(pending.props)
            ? (result ?? {
                confirmed: true,
                checked: defaultChecked(pending.props.checkboxes),
              })
            : true,
        )
      }
      cancel={() => settle(refused(pending.props))}
    />
  );
}

/** Hosts confirms opened while this dialog is on screen. */
export function ConfirmScope(props: { children: React.ReactNode }) {
  const depth = useContext(NestingContext) + 1;
  return (
    <NestingContext.Provider value={depth}>
      {props.children}
      <ConfirmOutlet />
    </NestingContext.Provider>
  );
}

function requestConfirm(props: ConfirmProps) {
  return new Promise<ConfirmResult | boolean>((resolve) => {
    const host = deepestHost();
    if (host === null) {
      // Refusing is the safe default: every caller guards its action on the result.
      // eslint-disable-next-line no-console
      console.error("confirm() was called with no <ConfirmOutlet /> mounted");
      resolve(refused(props));
      return;
    }
    host.open({ props, settle: resolve });
  });
}

const ConfirmModal = (props: ConfirmComponentProps) => {
  const confirmButtonText = props.confirmButtonText ?? "Confirm";
  const checkboxes = props.checkboxes ?? [];

  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const firstBlockingRef = useRef<HTMLInputElement>(null);
  const [checked, setChecked] = useState(() => defaultChecked(checkboxes));

  const blockingCheckbox = checkboxes.find(
    (checkbox) => checkbox.required === true && checked[checkbox.id] !== true,
  );
  const confirmBlocked = blockingCheckbox !== undefined;

  useMountEffect(() => {
    if (props.initialFocus === "cancel") {
      cancelButtonRef.current?.focus();
    } else if (confirmBlocked) {
      // Focusing a disabled button silently drops focus on the body, leaving the
      // dialog unreachable by keyboard.
      firstBlockingRef.current?.focus();
    } else {
      confirmButtonRef.current?.focus();
    }
  });

  const handleConfirm = () => {
    // `disabled` on our Button is a tw-classed variant, so it styles the button but
    // never reaches the DOM: the click is blocked by `pointer-events-none`, which does
    // nothing for keyboard activation. Refuse here so Enter on a focused button cannot
    // confirm an unacknowledged action.
    if (confirmBlocked) {
      return;
    }
    if (props.checkboxes !== undefined) {
      props.confirm({ confirmed: true, checked });
    } else {
      props.confirm();
    }
  };

  return (
    <Modal isOpen onClose={props.cancel}>
      <ModalHeader className="flex items-center gap-4">
        <span>{props.title}</span>
      </ModalHeader>
      <ModalBody>
        <div className="space-y-4">
          <div>{props.message}</div>
          {checkboxes.map((checkbox) => (
            <label
              key={checkbox.id}
              className="flex cursor-pointer items-center gap-2"
            >
              <input
                ref={
                  checkbox.id === blockingCheckbox?.id
                    ? firstBlockingRef
                    : undefined
                }
                type="checkbox"
                className="form-checkbox rounded-xs border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600"
                checked={checked[checkbox.id] ?? false}
                onChange={(e) =>
                  setChecked((current) => ({
                    ...current,
                    [checkbox.id]: e.target.checked,
                  }))
                }
              />
              <span className="text-sm">{checkbox.label}</span>
            </label>
          ))}
        </div>
      </ModalBody>
      <ModalFooter className="flex-row-reverse justify-start gap-4">
        <PrimaryButton
          ref={confirmButtonRef}
          colorScheme={props.type === "danger" ? "danger" : "primary"}
          disabled={confirmBlocked}
          aria-disabled={confirmBlocked}
          onClick={handleConfirm}
        >
          {confirmButtonText}
        </PrimaryButton>
        <SecondaryButton
          ref={cancelButtonRef}
          onClick={() => {
            props.cancel();
          }}
        >
          Cancel
        </SecondaryButton>
      </ModalFooter>
    </Modal>
  );
};

export const confirm = requestConfirm as (
  props: Omit<ConfirmProps, "checkboxes">,
) => Promise<boolean>;

export const confirmWithCheckboxes = requestConfirm as (
  props: ConfirmProps & { checkboxes: ConfirmCheckboxOption[] },
) => Promise<ConfirmResult>;
